import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, KeyboardEventHandler, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Key } from 'record-it-utils/utils/Key/Key';
import { ButtonStandard, ButtonStandardHoverEffectType, ButtonStandardType } from '../../button/ButtonStandard/ButtonStandard';
import { CardBody } from '../../card/CardBody/CardBody';
import { CardHeader, HorizontalAlignment } from '../../card/CardHeader/CardHeader';
import { CardStandard, CardStandardProps } from '../../card/CardStandard/CardStandard';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useRefWithCurrentValue } from '../../hooks/useRefWithCurrentValue';
import { TextInput, TextInputType } from '../../input/TextInput/TextInput';
import { LoadingOverlay } from '../../misc/LoadingOverlay/LoadingOverlay';
import { MessageBox, MessageBoxType } from '../../misc/MessageBox/MessageBox';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { FormGroup } from '../FormGroup/FormGroup';
import styles from './LoginForm.module.css';
export type LoginFormProps = PropsWithoutChildren<CardStandardProps> & {
  login: LoginFunction;
  onForgotPassword: (email: string) => void;
  onCreateAccount: () => void;
  errorMessage?: string;
};
export const LoginForm: FunctionComponent<LoginFormProps> = React.memo(props => {
  const $ = _c(57);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let errorMessage;
  let login;
  let onForgotPassword;
  let remainingProps;
  if ($[2] !== t0) {
    const {
      login: t1,
      onCreateAccount,
      onForgotPassword: t2,
      errorMessage: t3,
      ...t4
    } = t0;
    login = t1;
    onForgotPassword = t2;
    errorMessage = t3;
    remainingProps = t4;
    $[2] = t0;
    $[3] = errorMessage;
    $[4] = login;
    $[5] = onForgotPassword;
    $[6] = remainingProps;
  } else {
    errorMessage = $[3];
    login = $[4];
    onForgotPassword = $[5];
    remainingProps = $[6];
  }
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const isLoggingInRef = useRefWithCurrentValue(isLoggingIn);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const {
    t
  } = useTranslation("record-it-components");
  let t1;
  if ($[7] !== email || $[8] !== isLoggingInRef || $[9] !== login || $[10] !== password) {
    t1 = () => {
      if (isLoggingInRef.current) {
        return;
      }
      setIsLoggingIn(true);
      login(email, password).finally(() => {
        setIsLoggingIn(false);
      });
    };
    $[7] = email;
    $[8] = isLoggingInRef;
    $[9] = login;
    $[10] = password;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  const handleLoginClick = useEventCallback(t1);
  let t2;
  if ($[12] !== handleLoginClick) {
    t2 = event => {
      if (event.key === Key.Enter) {
        handleLoginClick();
      }
    };
    $[12] = handleLoginClick;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const handlePasswordKeyDown = t2;
  let t3;
  if ($[14] !== email || $[15] !== onForgotPassword) {
    t3 = () => {
      onForgotPassword(email);
    };
    $[14] = email;
    $[15] = onForgotPassword;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  const handleForgotPasswordClick = useEventCallback(t3);
  let t4;
  if ($[17] !== t) {
    t4 = t("form.LoginForm.title");
    $[17] = t;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t4) {
    t5 = <CardHeader textAlignment={HorizontalAlignment.Center}>{t4}</CardHeader>;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== errorMessage) {
    t6 = errorMessage ? <MessageBox type={MessageBoxType.Error} className={styles.messageBox} data-testid="LoginForm.errorMessageBox">{errorMessage}</MessageBox> : null;
    $[21] = errorMessage;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t) {
    t7 = t("form.LoginForm.emailLabel");
    $[23] = t;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== email || $[26] !== t7) {
    t8 = <FormGroup><TextInput label={t7} type={TextInputType.Email} value={email} onInput={setEmail} data-testid="LoginForm.emailTextInput" /></FormGroup>;
    $[25] = email;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t) {
    t9 = t("form.LoginForm.passwordLabel");
    $[28] = t;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== handlePasswordKeyDown || $[31] !== password || $[32] !== t9) {
    t10 = <FormGroup><TextInput label={t9} type={TextInputType.Password} value={password} onInput={setPassword} onKeyDown={handlePasswordKeyDown} data-testid="LoginForm.passwordTextInput" /></FormGroup>;
    $[30] = handlePasswordKeyDown;
    $[31] = password;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t) {
    t11 = t("form.LoginForm.forgotPassword");
    $[34] = t;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== handleForgotPasswordClick || $[37] !== t11) {
    t12 = <div className={styles.forgotPasswordButtonWrapper}><ButtonStandard type={ButtonStandardType.TextSecondary} hoverEffectType={ButtonStandardHoverEffectType.HalfTransparent} onClick={handleForgotPasswordClick} data-testid="LoginForm.forgotPasswordButton">{t11}</ButtonStandard></div>;
    $[36] = handleForgotPasswordClick;
    $[37] = t11;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== t) {
    t13 = t("form.LoginForm.loginButtonLabel");
    $[39] = t;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== handleLoginClick || $[42] !== t13) {
    t14 = <div className={styles.loginButtonWrapper}><ButtonStandard type={ButtonStandardType.Primary} onClick={handleLoginClick} data-testid="LoginForm.loginButton">{t13}</ButtonStandard></div>;
    $[41] = handleLoginClick;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== isLoggingIn) {
    t15 = <LoadingOverlay active={isLoggingIn} />;
    $[44] = isLoggingIn;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== t10 || $[47] !== t12 || $[48] !== t14 || $[49] !== t15 || $[50] !== t6 || $[51] !== t8) {
    t16 = <CardBody>{t6}{t8}{t10}{t12}{t14}{t15}</CardBody>;
    $[46] = t10;
    $[47] = t12;
    $[48] = t14;
    $[49] = t15;
    $[50] = t6;
    $[51] = t8;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== remainingProps || $[54] !== t16 || $[55] !== t5) {
    t17 = <CardStandard {...remainingProps} className={styles.card}>{t5}{t16}</CardStandard>;
    $[53] = remainingProps;
    $[54] = t16;
    $[55] = t5;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  return t17;
});
export type LoginFormI18N = {
  /** e.g. Login */
  title: string;
  /** e.g. E-Mail */
  emailLabel: string;
  /** e.g. Passwort */
  passwordLabel: string;
  /** e.g. Login */
  loginButtonLabel: string;
  /** e.g. Account erstellen */
  createAccount: string;
  /** e.g. Passwort vergessen */
  forgotPassword: string;
};
export type LoginFunction = (email: string, password: string) => Promise<void>;